import React, { useState, useEffect } from 'react';
import { Loader } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useSettings } from '@/hooks/useSettings';

/**
 * Terms & Conditions Management Component
 * Manage terms and conditions for the booking platform
 */
export const TermsAndConditions = ({ API }) => {
  const { settings, updatePaymentSettings } = useSettings(API);
  const [termsContent, setTermsContent] = useState('');
  const [termsLoading, setTermsLoading] = useState(false);

  // Default terms template - Kaiser Bridge Hotel Terms & Conditions
  const defaultTerms = `BOOKING CONDITIONS & TERMS
Kaiser Bridge Hotel

Please read these Booking Conditions and Terms carefully before making a reservation at Kaiser Bridge Hotel. By completing your booking, you agree to be bound by these terms.

═══════════════════════════════════════════════════════════════
BOOKING CONDITIONS
═══════════════════════════════════════════════════════════════

PAYMENT TERMS

Non-Refundable Rate:
Full payment is required at the time of booking. Please note that this reservation cannot be cancelled or modified, and no refund will be issued.

Flexible Rate:
• A deposit of 30% of the total reservation cost will be charged on the day of booking
• The remainder 70% of the total amount of your reservation will be paid at check-in time

═══════════════════════════════════════════════════════════════

CANCELLATION POLICY (for Flexible Rate)

• Cancellations received 30 days prior to arrival are free of charge
• Cancellations received between 30 days – 7 days are subject to a cancellation fee of 30% of the total agreed price of the reservation
• Cancellation received between 7 days – 3 days are subject to a cancellation fee of 70% of the total agreed price of the reservation
• Cancellation received between 3 days and arrival time are subject to a cancellation fee of 100% of the total agreed price of the reservation
• Non-show fee is subject to a cancellation fee of 100% of the total agreed price of the reservation

═══════════════════════════════════════════════════════════════

CHECK-IN & CHECK-OUT TIMES

• Check-in time: 15:00
• Check-out time: 11:00

═══════════════════════════════════════════════════════════════

CHILDREN POLICY

Minimum age accepted: 15 years

═══════════════════════════════════════════════════════════════

CLIMATE CHANGE RESILIENCE TAX

A 5€ Climate Change Resilience tax per night will be paid at check-in time

═══════════════════════════════════════════════════════════════

PET POLICY

Pets are not allowed

═══════════════════════════════════════════════════════════════
DETAILED TERMS & CONDITIONS
═══════════════════════════════════════════════════════════════

1. BOOKING AND PAYMENT TERMS

1.1 Booking Confirmation:
Your booking is confirmed once payment is processed or payment on arrival is selected. You will receive a confirmation email with your booking details.

1.2 Payment Options:
We offer multiple payment options:
• Flexible Rate: Pay a deposit now and the remaining balance upon arrival. Modifications allowed.
• Non-Refundable Rate: Pay the full amount in advance at a discounted rate. No modifications or refunds.
• Pay on Arrival: Full payment due at check-in. Modifications allowed.

1.3 Payment Security:
All card payments are processed through Nexi XPay of Nexi Payments Greece S.A., using TLS 1.2 encryption with 128-bit SSL for maximum security.

═══════════════════════════════════════════════════════════════

2. CANCELLATION AND MODIFICATION POLICY

2.1 Flexible Rate Bookings:
• Free cancellation up to 7 days before check-in date
• Cancellations made 3-7 days before check-in: 50% refund of deposit paid
• Cancellations made within 3 days of check-in: No refund
• Modifications to dates or guest count can be made subject to availability

2.2 Non-Refundable Rate Bookings:
• No cancellations or modifications allowed
• Full payment is non-refundable under any circumstances
• This rate offers a discount in exchange for booking flexibility

2.3 Pay on Arrival Bookings:
• Free cancellation up to 7 days before check-in
• Cancellations within 7 days may result in a no-show fee equal to one night's accommodation

2.4 No-Show Policy:
Failure to arrive on your check-in date without prior cancellation will result in the forfeiture of your entire booking amount.

═══════════════════════════════════════════════════════════════

3. CHECK-IN AND CHECK-OUT

3.1 Check-in Time:
Standard check-in is from 2:00 PM. Early check-in subject to availability and may incur additional charges.

3.2 Check-out Time:
Check-out is by 11:00 AM. Late check-out subject to availability and may incur additional charges.

3.3 Identification:
Valid photo identification and credit card required at check-in.

═══════════════════════════════════════════════════════════════

4. GUEST RESPONSIBILITIES

4.1 Property Care:
Guests are responsible for any damage caused to the room, facilities, or hotel property during their stay.

4.2 Conduct:
Guests must respect other guests and hotel staff. The hotel reserves the right to refuse service or ask guests to leave if behavior is deemed inappropriate.

4.3 Smoking Policy:
Smoking is prohibited in all rooms and indoor areas. Violation may result in cleaning fees.

4.4 Maximum Occupancy:
Number of guests must not exceed the maximum occupancy for the room type booked.

═══════════════════════════════════════════════════════════════

5. PRIVACY AND DATA PROTECTION

5.1 Data Collection:
We collect and process your personal information in accordance with GDPR and applicable privacy laws.

5.2 Data Usage:
Your information is used solely for booking management, communication, and improving our services.

5.3 Data Security:
We implement appropriate security measures to protect your personal information.

═══════════════════════════════════════════════════════════════

6. LIMITATION OF LIABILITY

6.1 Personal Belongings:
The hotel is not responsible for loss or damage to guests' personal belongings unless deposited in the hotel safe.

6.2 Force Majeure:
The hotel is not liable for failure to perform obligations due to circumstances beyond our control (natural disasters, government actions, etc.).

═══════════════════════════════════════════════════════════════

7. CONTACT INFORMATION

Kaiser Bridge Hotel
Benitses, Corfu, Greece
Tel: +30 26610 72120
Email: info@corfuhotelkaiser.com

═══════════════════════════════════════════════════════════════

IMPORTANT: By clicking "I Accept" and completing your booking, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions, including our cancellation and refund policies.

═══════════════════════════════════════════════════════════════
Last Updated: ${new Date().toLocaleDateString()}
═══════════════════════════════════════════════════════════════`;

  // Load terms from settings when component mounts or settings change
  useEffect(() => {
    if (settings.terms_and_conditions) {
      setTermsContent(settings.terms_and_conditions);
    } else {
      setTermsContent(defaultTerms);
    }
  }, [settings]);

  const handleSaveTerms = async () => {
    if (!termsContent.trim()) {
      toast.error('Terms and conditions cannot be empty');
      return;
    }
    
    try {
      setTermsLoading(true);
      const result = await updatePaymentSettings({ terms_and_conditions: termsContent });
      if (result.success) {
        toast.success('Terms and conditions updated successfully');
      }
    } catch (error) {
      console.error('Error saving terms:', error);
      toast.error('Failed to save terms and conditions');
    } finally {
      setTermsLoading(false);
    }
  };

  const handleResetTerms = () => {
    if (window.confirm('Reset to default terms and conditions?')) {
      setTermsContent(defaultTerms);
      toast.success('Terms reset to default');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="space-y-6">
        <div className="pb-4 border-b">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <span className="text-2xl">📋</span>
            Terms & Conditions Management
          </h3>
          <p className="text-sm text-gray-600 mt-1">Manage terms and conditions for your booking platform</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Terms & Conditions</h4>
          <p className="text-sm text-blue-800">
            Configure the terms and conditions that customers must accept when making bookings.
            This content will be displayed during the booking process.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="terms_content" className="text-base font-medium">
              Terms & Conditions Content
            </Label>
            <textarea
              id="terms_content"
              rows={15}
              value={termsContent}
              onChange={(e) => setTermsContent(e.target.value)}
              className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              placeholder="Enter your terms and conditions here..."
            />
            <p className="text-xs text-gray-500 mt-1">
              This content will be displayed to customers during the booking process
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSaveTerms}
              disabled={termsLoading}
              className="bg-gradient-to-r from-blue-600 to-indigo-600"
            >
              {termsLoading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Terms & Conditions'
              )}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleResetTerms}
              disabled={termsLoading}
            >
              Reset to Default
            </Button>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes</h4>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• Terms and conditions are legally binding</li>
            <li>• Consult with legal counsel for your specific jurisdiction</li>
            <li>• Update terms regularly to reflect current policies</li>
            <li>• Ensure terms are clearly displayed during booking process</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
